import * as React from "react";
import useApi from "hooks/use-api";
import {useCallback, useEffect, useState} from "react";
import {paths} from "paths";
import {enqueueSnackbar} from "notistack";
import {Navigate, useNavigate, useParams} from "react-router-dom";
import Stack from "@mui/material/Stack";
import Organisation from "benchmarking-domain/organisation";
import { OrganisationForm } from "components/admin/organisations/organisation-form";
import Plant from "benchmarking-domain/plant";
import { PlantForm } from "components/admin/organisations/plant-form";

const OrganisationFormMemo = React.memo(OrganisationForm);
const PlantFormMemo = React.memo(PlantForm);

export default function OrganisationDetails(): React.JSX.Element {

  const { organisationId } = useParams();
  const navigate = useNavigate();
  const { subscribeOrganisation, saveOrganisation, subscribePlants, savePlant } = useApi();
  const [organisation, setOrganisation] = useState<Organisation>();
  const [plants, setPlants] = useState<Plant[]>();

  useEffect(() => {
    if (!organisationId) {
      navigate(paths.errors.notFound, { replace: true});
      return;
    }

    const unsubscribeOrganisation = subscribeOrganisation(organisationId, (organisation) => organisation ? setOrganisation(organisation) : navigate(paths.errors.notFound, { replace: true }));
    const unsubscribePlants = subscribePlants(organisationId, setPlants);

    return () => {
      unsubscribeOrganisation();
      unsubscribePlants();
    }
  }, [navigate, organisationId, subscribeOrganisation, subscribePlants]);

  const submitOrganisationChanges = useCallback(async (data: Organisation) => {
    try {
      await saveOrganisation({...data, id: organisationId!});
      enqueueSnackbar('Organisation changed!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Organisation save failed! ' + error, { variant: 'error' });
    }

  }, [organisationId, saveOrganisation])

  const submitPlantChanges = useCallback(async (data: Plant) => {
    try {
      await savePlant(organisationId!, {...data });
      enqueueSnackbar('Plant changed!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Plant save failed! ' + error, { variant: 'error' });
    }

  }, [organisationId, savePlant])

  if(!organisation)
    return <>Loading...</>;

  return <Stack spacing={3}>
    <OrganisationFormMemo organisation={organisation} onSubmit={submitOrganisationChanges} />
    {plants && plants.length > 0 && plants.sort((one, two) => (one.name < two.name ? -1 : 1)).map(plant => {
      return <PlantFormMemo key={plant.id} organisationId={organisation.id} plant={plant} onSubmit={submitPlantChanges}/>;
    })}
    <PlantFormMemo organisationId={organisation.id} onSubmit={submitPlantChanges}/>
  </Stack>;
}
